import {Component, ElementRef, EventEmitter, OnInit, AfterViewInit} from '@angular/core';
import {StepComponent} from "../../step/step.component";
import {TranslateService} from "@ngx-translate/core";
import {DecimalPipe} from "@angular/common";
import {CostRoundPipe} from "../../pipe/CostRoundPipe";
import {EmbedService} from "../../services/embed.service";
import {StepService} from "../../services/step.service";
import {Router} from "@angular/router";
import {HeizcheckRequestService} from "../services/heizcheck-request.service";
import {ElementService} from "../../services/element.service";
import {HeizcheckAnfrage} from "../anfrage/heizcheck.anfrage";
import {HeizcheckConstants} from "../heizcheck.constants";
import {PortalService} from "../../services/portal.service";
import {ErgebnisComponent} from "../ergebnis/ergebnis.component";
import {EnergyMapService} from "../../energy-map/services/energy.map.service";
import {HeizkostenabrechnungForm} from "../heizkostenabrechnung/heizkostenabrechnung.form";

/**
 * Special component for the start page of the chapter "verbrauchsanalyse". Only Textoutputs, no form.
 */
@Component({
    selector: 'app-heizcheck-auswerten',
    templateUrl: './auswerten.component.html',
    providers: [DecimalPipe, CostRoundPipe],
    standalone: false
})
export class AuswertenComponent extends StepComponent implements OnInit, AfterViewInit {

  /**
   * the current userdata object
   */
  anfrage: HeizcheckAnfrage;

  /**
   * Holds the calculated result that has come from the backend.
   */
  calculationResult: any;

  /**
   * Result Object. Holds several paths of the result and the last value has to be someting of (-2|-1|0|+1|+2)
   */
  result: any = {
    entry: {
      consumption: "n/a",
      energy: "n/a",
      building: "n/a",
      summary: "n/a"
    },
    consumption: {
      overall: "n/a",
      heating: "n/a",
      water: "n/a"
    },
    energy: {
      overall: "n/a",
      price: "n/a",
      costs: "n/a"
    },
    building: {
      overall: "n/a",
      condition: "n/a"
    }
  }

  /**
   * list of placehodlers for texts of the result page
   */
  placeholder: any = {
    entry: {},
    consumption: {},
    consumption_heating: {},
    consumption_water: {},
    energy: {},
    energy_price: {},
    energy_costs: {},
    building: {},
    co2: {},
  }

  /**
   * list of available tipps
   *
   * FOR DEV: you can set "open: true" in any hint to open it initially.
   */
  tipps: any = {
    consumption: [
      {name: "temperature", saving: 35, fast: false, co2: 150},
      {name: "savingshower", saving: 215, fast: false, co2: 425},
      {name: "ventilate", saving: 80, fast: false, co2: 315},
      {name: "shower", saving: 35, fast: false, co2: 115},
      {name: "thermostat", saving: 60, fast: false, co2: 250},
      {name: "waterregulator", saving: 35, fast: false, co2: 65},
      {name: "coldwaterhand", saving: 20, fast: false, co2: 75},
      {name: "sealwindow", saving: 35, fast: false, co2: 130},
      {name: "savewater", saving: 360, fast: false, co2: 365},
    ]
  }

  /**
   * List of tipps we want to show for internal portals
   */
  tippsInternal: string[] = ["ventilate", "shower", "savewater", "temperature"];

  /**
   * toggle if we have data to show or not.
   */
  hasData: boolean = false;

  /**
   * toggle for some special settings if decentralized water heating
   */
  decentralizedWaterHeating: boolean = false;

  /**
   * toggle to display special content for older buildings.
   */
  oldBuilding: boolean = false;

  /**
   * textkey for the energy teaser
   */
  energyTeaser: string;

  /**
   * toggle if this is an internal portalId or not
   */
  internalPortal: boolean = false;

  /**
   * hide report download
   */
  hideReportDownload: boolean;

  /**
   * Todo: Holds the reportUrl if the report is offered as download
   */
  reportUrl: string = null;

  /**
   * determines path for previous page
   */
  previousPage: string = null;

  /**
   * Holds the data that we need for the dialogform.
   */
  dialogData: any = {};

  /**
   * data for different cases to show different info texts
   */
  dataCases: {isFossilEnergySource: {fossil: boolean, gas: boolean, oil: boolean}, isDistrictHeating: boolean};

  /**
   * Data for comparing the actual costs with the future estimation
   */
  compareData: {
    costs: {value: number, percent: number}} = {costs: {value: 0, percent: 0.5}};

  /**
   * list of data for the forcast years
   */
  forecastData: {
    market: { year: string, energy: number, co2: number, costs: number, extraCosts: number, percentage?: number }[],
    real: { year: string, energy: number, co2: number, costs: number, extraCosts: number, percentage?: number }[],
    activeType: 'market' | 'real'
  } = {market: [], real: [], activeType: 'real'};

  /**
   * toggle if we have fossil energy source or not
   */
  isFossilEnergySource: boolean = true;

  /**
   * toggle if we have district heating as energy source or not
   */
  isDistrictHeating: boolean = false;

  /**
   * Toggle to force the Dialog/NL-Box
   */
  forceDialog: boolean = false;

  /**
   * toggle for hiding the tipps
   */
  hideTipps: boolean = false;

  /**
   * Toogle for showhing/hiding hints
   */
  hints: {[key: string]: boolean} = {avg: false, costs: false, estimate: false, forecast: false};

  /**
   * constructor
   * @param stepService
   * @param router
   * @param portalService
   * @param element
   * @param requestService
   * @param translate
   * @param elementService
   * @param decimalPipe
   * @param costRoundPipe
   * @param embedService
   * @param energyMapService
   */
  constructor(protected stepService: StepService, protected router: Router, protected requestService: HeizcheckRequestService, protected portalService: PortalService,
              private element: ElementRef, private translate: TranslateService, protected elementService: ElementService,
              private decimalPipe: DecimalPipe, private costRoundPipe: CostRoundPipe, private embedService: EmbedService,
              protected energyMapService: EnergyMapService)
  {
    super(stepService, router, elementService);
  }

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    super.ngOnInit();

    this.anfrage = this.requestService.getAnfrage();

    // init backend placeholder and merge with placeholder
    const backendPlacehoder = this.anfrage.heizkostenanalyseResult?.result?.placeholder ?? {};
    this.placeholder = {...backendPlacehoder, ...this.placeholder};

    this.calculationResult = this.anfrage.heizkostenanalyseResult?.result?.data?.abrechnung;
    if(!this.calculationResult || this.anfrage.heizkostenanalyseResult?.result?.data.hasOwnProperty('id') === false) {
      console.error('Could not find calculation result');
    }
    else {
      this.hasData = true;
    }

    // init calculation and placeholder
    this.initResult();

    // check if the current portal is internal
    this.internalPortal = this.portalService.isInternalPortal();
    this.hideReportDownload = this.portalService.getConfigAsBool('embed.newsletter.report.download');

    // set download paths and init dialog data
    this.reportUrl = this.anfrage.heizkostenanalyseResult?.result?.report?? null;
    this.initDialogData();

    // init forcast data
    this.initForecast();

    // should the nl module forced to be visible
    this.forceDialog = this.portalService.getConfigAsBool('embed.newsletter.force');

    /// should the tipps be hidden?
    this.hideTipps = this.portalService.getConfigAsBool('heizcheck.tipps.hide');

    if (this.energyMapService.showEnergyMap(this.anfrage, 'heizcheck') === true
        && this.energyMapService.isEnergyMapDataDonated(this.anfrage) === false
        && this.energyMapService.getEnergyMapConfirmDonation(this.anfrage) !== 'n'
        && this.energyMapService.getEnergyMapBuildingNotIncludedInMap(this.anfrage) !== true
    ) {
      this.previousPage = '../../heizkostenabrechnung/energy-map';
    }
    else if(this.anfrage.heizkostenanalyseInput.heiznebenkosten_messgeraet_art === "verdunster") {
      // if input value for 'heiznebenkosten_messgeraet_art' === "verdunster" previous page will be heizkostenabrechnung/sonstiges/1
      this.previousPage = "../../heizkostenabrechnung/sonstiges/1";
    }
    else {
      this.previousPage = "../../heizkostenabrechnung/sonstiges/3";
    }

    this.updateBars();
   }

  /**
   * initialize the forcast data for the chart
   */
  initForecast(): void {
    if(Object.keys(this.anfrage.heizkostenanalyseResult?.result?.data?.prognose).length > 0) {
      // determine some placeholder for dynamic text
      this.placeholder.co2percent_tenant = this.anfrage.heizkostenanalyseResult.result.data.prognose?.co2_kosten?.splitting?.prozent_mieter?? null;
      this.placeholder.lastyear = this.anfrage.heizkostenanalyseResult.result.data.abrechnung?.abrechnungsjahr_anteil?? null;

      // get the forecast data from the result object
      this.isFossilEnergySource = this.anfrage.heizkostenanalyseResult.result.data.prognose?.co2_kosten?.aktuell_wohnung?.kosten > 0;
      const prognose = this.anfrage.heizkostenanalyseResult.result.data.prognose;
      this.forecastData.real = ErgebnisComponent.buildForcastData(prognose, 'real', 'zukunft_wohnung');

      // current user data to compare with the forecast data
      this.compareData.costs = {
        value: this.anfrage.heizkostenanalyseResult.result.data.prognose?.gesamtkosten_real?.aktuell?.anteil_prozent?? null,
        percent: .5,
      };
    }

    // different cases for the forecast chart component
    const energietraeger = this.anfrage?.heizkostenanalyseInput?.energietraeger_name;

    this.dataCases = {
      isFossilEnergySource: {
        fossil: this.isFossilEnergySource,
        gas: energietraeger === 'erdgas' && this.isFossilEnergySource,
        oil: energietraeger === 'heizoel' && this.isFossilEnergySource
      },
      isDistrictHeating: energietraeger === 'fernwaerme'
    };
  }

  /**
   * initialize the tipplist based on the users input and the calculation result.
   * @private
   */
  initTipps(): void {
    const userInput = this.anfrage.heizkostenanalyseInput;

    // remove some tipps because of the water heating
    if(this.decentralizedWaterHeating === false) {
      this.tipps.consumption.splice(this.tipps.consumption.findIndex(element => element.name === 'savewater'), 1);
    }
    else {
      // it's decentralized warmwater heating
      this.tipps.consumption.splice(this.tipps.consumption.findIndex(element => element.name === 'savingshower'), 1);
    }

    // add tipp saving and placeholder base on the household size
    const waterCalculation = [
      // {tipp: 'savingshower', saving: 108, co2: 213},
      {tipp: 'shower', saving: 16.4, co2: 56.7},
      {tipp: 'waterregulator', saving: 18, co2: 33},
      {tipp: 'coldwaterhand', saving: 11, co2: 36},
      {tipp: 'savewater', saving: 180, co2: 183},
    ];
    const persons = parseInt(userInput['bewohneranzahl']);
    for(const row of waterCalculation) {
      const tipp = this.tipps.consumption.find(element => element.name === row.tipp);
      if(tipp) {
        tipp.saving = this.decimalPipe.transform(Math.floor(row.saving * persons), '1.0-0');
        tipp.co2 = Math.round(row.co2 * persons);
        tipp.placeholder = {
          saving: this.decimalPipe.transform(Math.floor(row.saving * persons), '1.0-1'),
          co2: this.decimalPipe.transform(row.co2 * persons, '1.0-0'),
          trees: this.decimalPipe.transform(Math.floor((row.co2 * persons) / HeizcheckConstants.CO2_TREE_KG_YEAR), '1.0-1'),
          persons: persons
        };
      }
    }

    // special case: saving shower: Saving is calculated based on the real consumption.
    const co2Factor = this.calculationResult['co2emissionen_faktor_kg_je_kwh'];
    const tipp = this.tipps.consumption.find(element => element.name === 'savingshower');
    if(tipp) {
      const saving = ((this.calculationResult['wohnung_heizkosten_warmwasser_cent_je_m2_monat'] * 12 * userInput['wohnung_flaeche']) / 100) * 0.6 * 0.5;
      tipp.saving = this.decimalPipe.transform(Math.floor(saving), '1.0-0');
      tipp.co2 = Math.round(this.calculationResult['wohnung_verbrauch_warmwasser_kwh'] * 0.6 * 0.5 * co2Factor);
      tipp.placeholder = {
        saving: tipp.saving,
        co2: this.decimalPipe.transform(tipp.co2, '1.0-0'),
        trees: this.decimalPipe.transform(Math.floor(tipp.co2 / HeizcheckConstants.CO2_TREE_KG_YEAR), '1.0-1'),
        persons: persons
      };
    }

    // reorder tipps based on the calculation results
    this.tipps.consumption.sort((a, b) => {
      if(parseInt(this.result.consumption.heating) > 0 && (a.name === "thermostat" || b.name === "thermostat")) {
        // bring up tzhe best heating tipp
        return a.name === "thermostat"? -1 : 1;
      }

      return a.saving > b.saving? -1 : 1;
    });

    // reduce the tipss list to 3 items for internal portal
    if(this.portalService.isInternalPortal() === true) {
      this.tipps.consumption = this.tipps.consumption.filter(tipp => this.tippsInternal.indexOf(tipp.name) > -1);
      this.tipps.consumption.splice(3);
    }

    // set marker for best climate tipp based on the order
    let bestIndex = 0;
    let co2 = -1;
    for(let i=0; i<3; i++) {
      if(this.tipps.consumption[i].co2 > co2) {
        co2 = this.tipps.consumption[i].co2;
        bestIndex = i;
      }
    }
    this.tipps.consumption[bestIndex].hasCo2Marker = true;
  }

  /**
   * Initialize this.result and this.placeholder based on the calculation that we get from the backend.
   * @private
   */
  private initResult(): void {
    const userInput = this.anfrage.heizkostenanalyseInput;

    if(!userInput || !this.calculationResult) {
      // do nothing, no data there
      return;
    }

    // check if central or decentralized water heating
    this.decentralizedWaterHeating = userInput['warmwasser_bereitungsart'] === 'dezentral';

    // toggle for old buildings
    this.oldBuilding = parseFloat(userInput['gebaeudebaujahr']) < HeizcheckConstants.OLD_BUILDING_MAXYEAR;

    // special textkey for the energy teaser
    this.energyTeaser = 'heizcheck.auswerten.energy.teaser.leitung';
    if(['heizoel', 'holzpellets'].indexOf(userInput['energietraeger_name']) !== -1) {
      this.energyTeaser = 'heizcheck.auswerten.energy.teaser.keineleitung';
    }

    /* Consumption */
    const consumptionDeviation = this.calculationResult['wohnung_verbrauch_gesamt_abweichung_prozent'];
    const consumptionHeatingDeviation = this.calculationResult['wohnung_verbrauch_raumwaerme_abweichung_prozent'];
    const consumptionWaterDeviation = this.calculationResult['wohnung_verbrauch_warmwasser_liter_pro_person_tag_abweichung_prozent'];
    // overall
    if(consumptionDeviation < -5) {
      this.result.consumption.overall = "-1";
    }
    else if(consumptionDeviation > 5) {
      this.result.consumption.overall = "+1";
    }
    else {
      this.result.consumption.overall = "0";
    }
    this.placeholder.consumption = {
      'percent': this.decimalPipe.transform(Math.abs(consumptionDeviation), '1.0-0'),
      'percent_rare': consumptionDeviation,
      'year': userInput['abrechnung_bis'].substr(6, 4)
    }
    // heating
    if(consumptionHeatingDeviation < -70) {
      this.result.consumption.heating = "-2";
    }
    else if(consumptionHeatingDeviation < -5) {
      this.result.consumption.heating = "-1";
    }
    else if(consumptionHeatingDeviation > 300) {
      this.result.consumption.heating = "+2";
    }
    else if(consumptionHeatingDeviation > 5) {
      this.result.consumption.heating = "+1";
    }
    else {
      this.result.consumption.heating = "0";
    }
    this.placeholder.consumption_heating = {
      'percent': this.decimalPipe.transform(Math.abs(consumptionHeatingDeviation), '1.0-0'),
      'eur': this.decimalPipe.transform(this.costRoundPipe.transform(this.calculationResult['wohnung_verbrauch_raumwaerme_differenz_euro_jahr'], true), '1.0-0'),
      'kwh': this.decimalPipe.transform(Math.abs(this.calculationResult['wohnung_verbrauch_raumwaerme_kwh_m2']), '1.0-0'),
    };
    // water
    if (consumptionWaterDeviation < -67) {
      this.result.consumption.water = "-2";
    } else if (consumptionWaterDeviation < -11) {
      this.result.consumption.water = "-1";
    } else if (consumptionWaterDeviation > 67) {
      this.result.consumption.water = "+2";
    } else if (consumptionWaterDeviation > 11) {
      this.result.consumption.water = "+1";
    } else {
      this.result.consumption.water = "0";
    }
    this.placeholder.consumption_water = {
      'liter': this.decimalPipe.transform(Math.abs(this.calculationResult['wohnung_verbrauch_warmwasser_liter_pro_person_tag']), '1.0-1'),
      'eur': this.decimalPipe.transform(this.costRoundPipe.transform(this.calculationResult['wohnung_verbrauch_warmwasser_differenz_euro_jahr'], true), '1.0-0'),
      'persons': userInput['bewohneranzahl'],
      'percent': this.decimalPipe.transform(Math.abs(consumptionWaterDeviation), '1.0-0'),
    };

    /* energy: overall */
    const energyDeviation = this.calculationResult['gebaeude_heizkosten_gesamt_abweichung_prozent'];

    this.result.energy.overall = "0";
    if(energyDeviation < -5) {
      this.result.energy.overall = "-1";
    }
    else if(energyDeviation > 5) {
      this.result.energy.overall = "+1";
    }
    this.placeholder.energy = {
      'eur': this.decimalPipe.transform(this.costRoundPipe.transform(Math.abs(this.calculationResult['gebaeude_heizkosten_gesamt_differenz_euro_jahr'])), '1.0-0'),
      'eur_rare': this.calculationResult['gebaeude_heizkosten_gesamt_differenz_euro_jahr']
    }

    /* energy: price */
    const energyPrice = this.calculationResult['gebaeude_energiepreis_cent_je_kwh'];
    const energyPriceAvg = this.calculationResult['gebaeude_energiepreis_cent_je_kwh_durchschnitt'];
    const energy = userInput.hasOwnProperty('energietraeger_name')? userInput['energietraeger_name'] : 'n/a';
    const energyPriceDeviation = this.calculationResult['gebaeude_energiepreis_abweichung_prozent'];

    // this.result.energy.price = this.anfrage.heizkostenabrechnungResult.result.ratings.energyprice;
    this.result.energy.price = this.anfrage.heizkostenanalyseResult?.result?.ratings?.energyprice?? '0';

    this.placeholder.energy_price = {
      'energietraeger': '',
      'energiepreis_abweichung': this.decimalPipe.transform(Math.abs(energyPriceDeviation), '1.0-0'),
      'eur': this.decimalPipe.transform(energyPrice, '1.1-1'),
      'eur_avg': this.decimalPipe.transform(energyPriceAvg, '1.1-1')
    }
    this.translate.get('heizcheck.energietraeger_name.' + energy + '.label').subscribe(data => {
      this.placeholder.energy_price['energietraeger'] = data;
      this.placeholder.energy['energietraeger'] = data;
      this.placeholder.building['energietraeger'] = data;
    });

    /* energy: costs */
    const costsDeviation = this.calculationResult['gebaeude_heiznebenkosten_gesamt_abweichung_prozent'];
    this.result.energy.costs = this.anfrage.heizkostenanalyseResult?.result?.ratings?.extracosts?? '0';

    this.placeholder.energy_costs = {
      'percent': this.decimalPipe.transform(Math.abs(costsDeviation), '1.0-0')
    }
    let msp = this.anfrage.heizkostenanalyseInput.messdienstleister;
    if(msp === 'sonstige') {
      msp += '.placeholder';
    }
    this.translate.get('heizcheck.messdienstleister.' + msp + '.label').subscribe(data => {
      this.placeholder.energy_costs['messdienstleister'] = data;
    });

    /* building */
    const evkw = this.calculationResult['gebaeude_evkw'];
    const evkwDeviation = this.calculationResult['gebaeude_evkw_abweichung_prozent'];
    const evkwMin = this.calculationResult['gebaeude_evkw_minimum'];
    const evkwMax = this.calculationResult['gebaeude_evkw_maximum'];
    const evkwAvg = this.calculationResult['gebaeude_evkw_durchschnitt'];

    if(evkwDeviation <= -11) {
      this.result.building.overall = "-1";
      this.result.building.condition = evkw < evkwMin? "-2" : "-1";
    }
    else if(evkwDeviation >= 11) {
      this.result.building.overall = "+1";
      this.result.building.condition = evkw > evkwMax? "+2" : "+1";
    }
    else {
      this.result.building.overall = "0";
      this.result.building.condition = "0";
    }

    // define which forcast data we will use for the placehoders
    const forcastIndex = 1;
    // calculate forecast costs
    let forecastCosts = this.anfrage.heizkostenanalyseResult.result.data?.prognose?.co2_kosten?.zukunft[forcastIndex]?.kosten_vermieter?? 0;
    forecastCosts = Math.round(forecastCosts * 10) / 10;

    // building placeholder
    this.placeholder.building = {...this.placeholder.building, ...{
      'evkw': Math.round(evkw),
      'evkw_percent': Math.abs(Math.round(evkwDeviation)),
      'evkw_min': Math.round(evkwMin),
      'evkw_max': Math.round(evkwMax),
      'evkw_avg': Math.round(evkwAvg),
      'evkw_2050': this.calculationResult['gebaeude_evkw_2045'],
      'co2': this.decimalPipe.transform(this.calculationResult['gebaeude_co2_tonnen_jahr'], '1.0-1'),
      'splitting_mieter': Math.round(this.anfrage.heizkostenanalyseResult.result.data?.prognose?.co2_kosten?.splitting?.prozent_mieter?? 0),
      'splitting_vermieter': Math.round(this.anfrage.heizkostenanalyseResult.result.data.prognose?.co2_kosten?.splitting?.prozent_vermieter?? 0),
      'prognose_jahr': this.anfrage.heizkostenanalyseResult.result.data?.prognose?.co2_kosten?.zukunft[1]?.jahr?? '',
      'prognose_kosten_vermieter': this.decimalPipe.transform(forecastCosts, '1.0-0'),
      'preissteigerung': this.decimalPipe.transform(this.anfrage.heizkostenanalyseResult.result.data?.prognose?.preissteigerung?.energiekosten?? 0, '1.0-1'),
      'lastyear': this.anfrage.verbrauchsanalyseResult.data.letzte.abrechnungsjahr
    }};

    /* entry */
    this.result.entry.energy = this.result.energy.overall;
    this.result.entry.consumption = this.result.consumption.overall;
    this.result.entry.building = this.result.building.overall;
    this.result.entry.summary = "0";
    if(this.result.consumption.overall === "-1" &&
      this.result.energy.overall === "-1" &&
      this.result.building.overall === "-1") {
      this.result.entry.summary = "-1";
    }

    // based on the users input, we need some placeholder from the translation bundle
    this.translate.get(['heizcheck.gebaeude', 'heizcheck.gebaeude.ihr']).subscribe(data => {
      this.placeholder.building['gebaeude_wohnungen'] = data['heizcheck.gebaeude'];
      this.placeholder.building['gebaeude_wohnung'] = data['heizcheck.gebaeude'];
      this.placeholder.building['ihr_gebaeude_wohnung'] = data['heizcheck.gebaeude.ihr'];
    });

    // co2 placeholder
    this.placeholder.co2 = {
      "co2": this.decimalPipe.transform(this.calculationResult['wohnung_co2_tonnen_jahr']?? 0, '1.0-1'),
      "trees": this.decimalPipe.transform(this.calculationResult['wohnung_co2_ausgleich_baeume_jahr']?? 0, '1.0-0')
    };

    this.initTipps();
  }

  /**
   * Returns the sign of a number, based if it is positiv or negativ
   * @param number
   */
  sign(number: number): string {
    if(number > 0) {
      return '+';
    }
    if(number < 0) {
      return '-';
    }
    return '';
  }

  /**
   * initialize the tooltips after the component has rendered
   */
  ngAfterViewInit(): void {
    super.ngAfterViewInit();

    // scroll to the top
    const element = document.querySelector("body");
    this.elementService.scrollToElement(element)

    // init tooltips
    this.elementService.initTooltips(this.element);
  }

  /**
   * Returns the textkey for a specific result value.
   * @param key It's a part of the result object. Something like 'entry.summary'
   * @param postfix optional extra postfix for this text
   */
  getTextKey(key: string, postfix: string = null): string {
    // basic part of the textkey
    let text = 'heizcheck.auswerten';

    // search for the result in the result object and add every part to the textkey.
    let value = this.result;
    key.split('.').forEach((part) => {
      text += '.' + part;
      value = value[part];
    })

    // add result as last part
    text += '.' + value;
    if(postfix != null) {
      text += '.' + postfix;
    }
    return text;
  }

  /**
   * Returns a single result value from the result object
   * @param key It's a part of the result object. Something like 'entry.summary'
   */
  getResult(key: string): string {
    let value = this.result;
    key.split('.').forEach((part) => {
      value = value[part];
    });
    return value;
  }

  /**
   * create data object that will be sent to the backend with the registration for the dialog
   */
  initDialogData(): void {
    this.requestService.backendService.getRequestId().subscribe(requestId => {
      this.dialogData = {
        requestId: requestId,
        bmOptinSource: window.location.href,
        doisource: 'hec',
        path: 'hka'
      }
    });
  }

  /**
   * eventhandler, after the dialog registration is finished.
   * @param result
   */
  dialogRegistered(result): void {
    const anfrage = this.requestService.getAnfrage();
    anfrage.dialogResult = result;

    this.requestService.setAnfrage(anfrage);
    this.requestService.saveDataToServer();
  }

  /**
   * Toggles the visibility of the hint box while closing all other hint boxes.
   * @param selectedKey
   */
  toggleHint(selectedKey: string): void {
    // current visibility state of the clicked item
    const currentState = this.hints[selectedKey];

    // 1. Reset all hints to false to ensure only one box is visible at a time
    Object.keys(this.hints).forEach(key => {
      this.hints[key] = false;
    });

    // 2. Toggle the clicked key based on its previous state
    this.hints[selectedKey] = !currentState;
  }

  /**
   * Update the bars height
   */
  updateBars(): void {
    // get the active forecast data
    const activeForecastData = this.forecastData[this.forecastData.activeType];

    // get all values together in one array from compareData and forecastData
    const allValues = [this.compareData.costs.value];
    activeForecastData.forEach(data => {
      allValues.push(data['costs']);
    });

    // get the highest value and calculate the relative height for each bar in percent
    const max = Math.max(...allValues);
    Object.keys(this.compareData).forEach(key => {
      this.compareData[key].percent = this.compareData[key].value / max;
    });

    activeForecastData.forEach(data => {
      data.percentage = data.costs / max;
    });
  }

}
